import { Component, ReactNode, Suspense, lazy, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useGetOrderChartsQuery } from 'redux/api/ordersApi';

import { OrderContext } from 'contexts/OrderContext';

import { usePriceVisible } from 'hooks/usePriceVisible';

import { formatCount } from 'helpers/parsePrice';

import { Headline } from 'components/Headline';
import { Loader } from 'components/Loader';

import * as S from './OrderCharts.styles';

// Lazy: keeps recharts out of the order page bundle until the section is opened.
const OrderChartsGrid = lazy(() =>
  import('./OrderChartsGrid').then((m) => ({ default: m.OrderChartsGrid }))
);

type ChartsErrorBoundaryProps = { fallback: ReactNode; children: ReactNode };

/**
 * Keeps a failure of the lazy charts chunk (e.g. a ChunkLoadError after a
 * deploy) or a chart render error inside the section, so the order page —
 * and any quantity edits in flight — survive it. The section remounts on every
 * open, which resets the boundary.
 */
class ChartsErrorBoundary extends Component<ChartsErrorBoundaryProps, { hasError: boolean }> {
  constructor(props: ChartsErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  render(): ReactNode {
    const { hasError } = this.state;
    const { fallback, children } = this.props;
    return hasError ? fallback : children;
  }
}

/**
 * The expanded "Show charts" section. Mounted only while open, so the charts
 * request runs on demand and again on every reopen (refetchOnMountOrArgChange).
 *
 * Freshness while open: quantity saves invalidate no cache tag (see
 * updateItemSizes) but do update the order's totals, so a revision of them is
 * part of the query arg. Each change is therefore a NEW request — a plain
 * refetch() would be dropped by RTK Query while a previous request for the same
 * arg is still pending, leaving the charts on pre-save numbers. The previous
 * charts stay on screen while the new revision loads. The revision covers every
 * price type, so switching the type in the table does not refetch (the payload
 * already carries all of them).
 */
function OrderChartsSection(): React.ReactElement {
  const { t } = useTranslation();
  const { id } = useParams();
  const { order, orderPriceType } = useContext(OrderContext);
  const priceVisible = usePriceVisible();

  const revision = `${order?.totalQuantity ?? ''}|${JSON.stringify(
    order?.resolvedTotals ?? {}
  )}`;
  const { data, isLoading, isError } = useGetOrderChartsQuery(
    { orderId: id ?? '', revision },
    { skip: !id, refetchOnMountOrArgChange: true }
  );

  // Opened from the bottom action bar the section is off screen — bring it in,
  // like the old catalogue's bottom "show charts" toggle did.
  const containerRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const { top } = el.getBoundingClientRect();
    if (top < 0 || top > window.innerHeight) {
      el.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // Same fallback as the order total in the header.
  const priceType = orderPriceType ?? 'WHOLESALE';
  const hasValue = priceVisible && !!data?.resolvedTotals[priceType];
  const valuePriceType = hasValue ? priceType : undefined;

  const loadError = (
    <Headline tag="p" scale="base" color="grayDarkSecondary">
      {t('orderCharts.loadError')}
    </Headline>
  );

  let content: React.ReactNode;
  if (isError) {
    content = loadError;
  } else if (!data || isLoading) {
    content = <Loader isLoading>{null}</Loader>;
  } else if (data.linesCount === 0) {
    content = (
      <Headline tag="p" scale="base" color="grayDarkSecondary">
        {t('orderCharts.noData')}
      </Headline>
    );
  } else {
    content = (
      <>
        <S.Summary>
          <Headline tag="h3" scale="base" color="blackLight" fontWeight="bold">
            {t('orderCharts.title')} — {t('orderCharts.lines')}: {formatCount(data.linesCount)}{' '}
            — {t('orderCharts.totalQuantity')}: {formatCount(data.totalQuantity)}
          </Headline>
        </S.Summary>
        <ChartsErrorBoundary fallback={loadError}>
          <Suspense fallback={<Loader isLoading>{null}</Loader>}>
            <OrderChartsGrid data={data} valuePriceType={valuePriceType} />
          </Suspense>
        </ChartsErrorBoundary>
      </>
    );
  }

  return (
    <S.Container
      ref={containerRef}
      aria-label={t('orderCharts.title')}
      data-testid="order-charts"
    >
      {content}
    </S.Container>
  );
}

/** "Show charts" section of the order page, toggled from the order action bars. */
export function OrderCharts(): React.ReactElement | null {
  const { isChartsOpen } = useContext(OrderContext);
  return isChartsOpen ? <OrderChartsSection /> : null;
}
